import { get } from "lodash";
import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import Icon, { IconName } from "src/components/Icon/Icon";
import LauncherModalV2 from "src/components/LauncherModalV2/LauncherModalV2";
import DisTable from "src/components/TableV2/Table";
import UploadDocumentModal from "src/components/UploadDocumentModalV2/UploadDocumentModalV2";
import useAutosave from "src/hooks/useAutosave";
import { useLoading } from "src/hooks/useLoading";
import { IApplicationDoc } from "src/store/models/application.model";
import {
  deleteAppDocument,
  downloadAppDocument,
  fetchAppDocuments,
  uploadAppDocument,
} from "src/store/reducers/applicationDocuments";
import { saveToDownloads } from "src/util/util.fileService";
import { formatDate } from "src/views/CampaignDetail/utils";
import * as S from "../../../../constants/StringConstants";
import { PageContainerButtons } from "../../PageContainer";
import { TabProps } from "../../pages";

export const AppDocuments = (props: TabProps) => {
  const [uploadModal, setUploadModal] = useState<boolean>(false);
  const { id: appID } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const fileTableRef = useRef<any>(null);
  const { setIsLoading } = useLoading();

  const uploadAppDoc = async (acceptedFiles: File[]) => {
    try {
      setIsLoading(true);
      for (const acceptedFile of acceptedFiles) {
        try {
          await dispatch(uploadAppDocument(appID, acceptedFile));
        } catch (e) {
          alert(S.APP_DOCS_UPLOAD_ERROR);
        } finally {
        }
      }

      if (fileTableRef) {
        fileTableRef.current.update({});
      }
    } catch (e) {
    } finally {
      setIsLoading(false);
    }
  };

  const deleteAppDoc = async (doc: IApplicationDoc) => {
    try {
      setIsLoading(true);
      await dispatch(deleteAppDocument(appID, `${doc.documentID}`));
      fileTableRef.current?.update();
    } catch (e) {
    } finally {
      setIsLoading(false);
    }
  };

  const downloadAppDoc = async (document: IApplicationDoc) => {
    try {
      setIsLoading(true);
      const result = await dispatch(downloadAppDocument(appID, `${document.documentID}`));
      const fileName = get(result, "value.headers.x-attachment-filename");
      const data = get(result, "value.data");

      if (fileName && data) {
        saveToDownloads(fileName, "application/pdf", data);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const AutoSave = useAutosave(props, () => {});

  const columns = [
    {
      key: "fileName",
      title: S.DOC_NAME_COL,
      style: { minWidth: "5rem" },
      render: item =>
        item.fileName && (
          <a
            className="app-docs__action-file-btn doc"
            title={item.fileName}
            onClick={async e => {
              e.preventDefault();
              await downloadAppDoc(item);
            }}>
            {item.fileName}
          </a>
        ),
    },
    { key: "modifiedBy", title: S.DOC_MODIFIED_BY_COL, style: { minWidth: "5rem" } },
    {
      key: "modifiedDate",
      title: S.APP_DOCS_COL_MODIFIED_DATE,
      style: { minWidth: "5rem" },
      render: (item: IApplicationDoc) => formatDate(item.modifiedDate),
    },
    {
      key: "download",
      title: S.APP_DOCS_COL_DOWNLOAD,
      style: { minWidth: "5rem" },
      render: item => {
        return (
          <div className="app-docs__action-container">
            <Icon name={IconName.DownloadFile} />

            <button
              className="app-docs__action-file-btn"
              onClick={async e => {
                e.preventDefault();
                await downloadAppDoc(item);
              }}>
              {S.DOC_DOWNLOAD_BUTTON}
            </button>
          </div>
        );
      },
    },
    {
      key: "delete",
      title: S.APP_DOCS_COL_DELETE,
      style: { minWidth: "5rem" },
      render: item => {
        return (
          <LauncherModalV2
            title={S.DOC_DELETE_CONFIRM_TEXT}
            launcher={
              <div className="app-docs__action-container">
                <Icon name={IconName.DeleteFileBlue} />

                <button
                  className="app-docs__action-file-btn"
                  onClick={e => {
                    e.preventDefault();
                  }}>
                  {S.DOC_DELETE_BUTTON}
                </button>
              </div>
            }
            onConfirm={() => deleteAppDoc(item)}
          />
        );
      },
    },
  ];

  return (
    <>
      <DisTable<IApplicationDoc>
        columns={columns}
        initialState={{}}
        getRef={ft => {
          fileTableRef.current = ft;
        }}
        onUpdate={async () => {
          const result = await dispatch(fetchAppDocuments(appID));

          return {
            total: get(result, "value.data.length") as number,
            data: get(result, "value.data") as IApplicationDoc[],
          };
        }}></DisTable>

      <UploadDocumentModal
        open={uploadModal}
        handleFiles={uploadAppDoc}
        onCancel={() => {
          setUploadModal(false);
        }}
      />

      <PageContainerButtons>
        <button className="button-v2 button-v2__secondary" onClick={() => setUploadModal(!uploadModal)}>
          {S.APP_DOCS_ADD_BUTTON_TITLE}
        </button>
      </PageContainerButtons>
      <AutoSave />
    </>
  );
};
